/**
 * Case Management Dashboard — landing page for legal/recovery staff.
 *
 * Renders KPI tiles, upcoming court dates, cases needing attention,
 * status/type breakdowns, recent activity, and quick-action shortcuts.
 *
 * Data comes from CaseManagementService (mock for the demo; HTTP later).
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CaseManagementService } from '../case-management.service';
import {
  CaseDashboardStats, CaseTypeBreakdown, CaseStatusBreakdown,
  CaseEvent, CaseActivity, LegalCase,
  EVENT_TYPE_LABELS, EVENT_TYPE_ICONS,
  CASE_TYPE_LABELS, CASE_PRIORITY_COLORS, CASE_STATUS_COLORS
} from '../case-management.model';

@Component({
  selector: 'sp-case-dashboard',
  templateUrl: './case-dashboard.component.html',
  styleUrls: ['./case-dashboard.component.scss']
})
export class CaseDashboardComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  isLoading = true;
  lastUpdated = new Date();

  stats: CaseDashboardStats = {
    activeCases: 0, hearingsThisWeek: 0, overdueActions: 0,
    closedThisMonth: 0, newThisWeek: 0,
    totalExposureKES: 0, recoveredYTDKES: 0, recoveryRatePct: 0
  };

  typeBreakdown: CaseTypeBreakdown[]   = [];
  statusBreakdown: CaseStatusBreakdown[] = [];
  upcomingEvents: CaseEvent[]          = [];
  recentActivity: CaseActivity[]       = [];
  needsAttention: LegalCase[]          = [];

  // Maps loaded once for template use
  readonly EVENT_LABELS = EVENT_TYPE_LABELS;
  readonly EVENT_ICONS  = EVENT_TYPE_ICONS;
  readonly TYPE_LABELS  = CASE_TYPE_LABELS;
  readonly PRIORITY_COLORS = CASE_PRIORITY_COLORS;
  readonly STATUS_COLORS = CASE_STATUS_COLORS;

  constructor(
    private caseService: CaseManagementService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAll(): void {
    this.isLoading = true;

    this.caseService.getDashboardStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => { this.stats = s; this.checkLoaded(); });

    this.caseService.getCaseTypeBreakdown()
      .pipe(takeUntil(this.destroy$))
      .subscribe(b => { this.typeBreakdown = b; this.checkLoaded(); });

    this.caseService.getCaseStatusBreakdown()
      .pipe(takeUntil(this.destroy$))
      .subscribe(b => { this.statusBreakdown = b; this.checkLoaded(); });

    this.caseService.getUpcomingEvents(14)
      .pipe(takeUntil(this.destroy$))
      .subscribe(e => { this.upcomingEvents = e; this.checkLoaded(); });

    this.caseService.getRecentActivity(10)
      .pipe(takeUntil(this.destroy$))
      .subscribe(a => { this.recentActivity = a; this.checkLoaded(); });

    this.caseService.getNeedsAttention(5)
      .pipe(takeUntil(this.destroy$))
      .subscribe(c => { this.needsAttention = c; this.checkLoaded(); });
  }

  private loaded = 0;
  private checkLoaded(): void {
    this.loaded += 1;
    if (this.loaded >= 6) {
      this.isLoading = false;
      this.lastUpdated = new Date();
      this.loaded = 0;
    }
  }

  refresh(): void { this.loadAll(); }

  // ── Navigation ───────────────────────────────────────────────────────────
  openCase(c: LegalCase): void {
    this.router.navigate(['/case-management/cases', c.id]);
  }
  openCaseList(): void {
    this.router.navigate(['/case-management/cases']);
  }
  openNewCase(): void {
    this.router.navigate(['/case-management/new']);
  }
  openEventCase(e: CaseEvent): void {
    this.router.navigate(['/case-management/cases', e.caseId], { fragment: 'events' });
  }
  exportReport(): void {
    // Demo placeholder — would call backend report API
    alert('Export Report — wires to /api/legal/dashboard/report (PDF) in production.');
  }

  // ── Format helpers ───────────────────────────────────────────────────────

  formatCurrency(value: number, compact: boolean = false): string {
    if (compact && Math.abs(value) >= 1_000_000) {
      return `KES ${(value / 1_000_000).toFixed(1)}M`;
    }
    if (compact && Math.abs(value) >= 1_000) {
      return `KES ${(value / 1_000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-KE', {
      style: 'currency', currency: 'KES',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-KE').format(value);
  }

  formatTimeAgo(date: Date): string {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60)    return 'Just now';
    if (seconds < 3600)  return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    const days = Math.floor(seconds / 86400);
    if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }

  formatEventTime(date: Date): string {
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    const tomorrow = new Date(now.getTime() + 86400000);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

    if (sameDay)    return `Today, ${time}`;
    if (isTomorrow) return `Tomorrow, ${time}`;

    return date.toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short'
    }) + `, ${time}`;
  }

  daysUntil(date: Date | undefined): number | null {
    if (!date) return null;
    return Math.ceil((date.getTime() - new Date().getTime()) / 86400000);
  }

  urgencyClass(days: number | null): string {
    if (days === null) return '';
    if (days < 0)  return 'urgency-overdue';
    if (days <= 2) return 'urgency-critical';
    if (days <= 7) return 'urgency-warning';
    return 'urgency-normal';
  }

  activityIcon(category: string): string {
    const icons: Record<string, string> = {
      OPENED: 'add_circle', CLOSED: 'check_circle',
      DOCUMENT: 'description', EVENT: 'event',
      TASK: 'check_box', PARTY: 'group',
      NOTE: 'sticky_note_2', COST: 'payments',
      STATUS: 'swap_horiz'
    };
    return icons[category] || 'history';
  }

  activityColorClass(category: string): string {
    return `act-${category.toLowerCase()}`;
  }

  maxStatusCount(): number {
    return Math.max(1, ...this.statusBreakdown.map(s => s.count));
  }

  totalCases(): number {
    return this.statusBreakdown.reduce((sum, s) => sum + s.count, 0);
  }

  statusPercent(count: number): number {
    const total = this.totalCases();
    return total === 0 ? 0 : Math.round((count / total) * 100);
  }
}
